import * as React from "react";

import "./bulk-mail-permit.scss";

import {XdSentry}       from "@Common/libs/sentry/xd-sentry";
import {cs}             from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {Load2}          from "@Common/react/load2";
import {UseState}       from "@Common/react/use-state";
import {BubbleNoti}     from "@Common/ui-components/bubble-noti/bubble-noti";

import {BULK_MAIL_PERMIT_PROHIBITED,       
        BULK_MAIL_PERMIT_USE_COMPANY} from "../../../common/logic/mailing/bulk-mail-permit";
import {COMPANY_LEVEL}                from "../../../common/logic/user-level";
import {MultiInputModal}              from "../multi-input-modal/multi-input-modal";

import {NoPermit} from "./no-permit/no-permit";

export const BulkMailPermit = ({apis: apis1, next}) => cs(
    consumeContext("company"),
    consumeContext("session"),
    consumeContext("userLevels"),
    ["permitActive", ({userLevels, company, session}, next) => next(
        bulkMailPermitIsAllowed(userLevels.userLevel, company, session)
    )],
    ["bulkMailPermit", ({permitActive}, next) => Load2({
        _key: !!permitActive,
        fetch: () => !permitActive ? null : apis1.getIndica(),
        next,
    })],

    ({permitActive, bulkMailPermit}) => next(permitActive && {
        loading: bulkMailPermit.loading,
        render: ({next}) => cs(
            consumeContext("apis"),
            ["loading", (_, next) => UseState({
                initValue: false,
                next
            })],
            ["bubbleNoti", (_, next) => BubbleNoti({next})],
            ["editPermitModal", ({loading, bubbleNoti}, next) => MultiInputModal({
                next,
                title: "Bulk Mail Permit",
                labelLocation: "left",
                onSave: async (updatePermitFields) => {
                    if(
                        updatePermitFields.line1 == "" ||
                        updatePermitFields.line2 == "" ||
                        updatePermitFields.line3 == ""
                    ) {
                        return {errorTop: "Permit information is not complete. Per USPS standards, permit must have at least 3 lines."};
                    } else {
                        try {
                            const result = await apis1.saveIndicia({
                                id: bulkMailPermit?.value?.idPrimary ?? 0,
                                indicia: updatePermitFields,
                            });
                            bubbleNoti.show({
                                message: `Success! Bulk mail permit successfully ${bulkMailPermit?.value?.idPrimary ? 'updated' : 'added'}.`,
                                color: "green",
                            });
                            return result;
                        } catch (error) {
                            if ((typeof error)=="string") {
                                return {errorMessage: error};
                            } else {
                                XdSentry.captureException(error);
                                return {errorMessage: "An unexpected error occurred."};
                            }
                        }
                    }
                },
                reload: async () => {
                    loading.onChange(true);
                    await bulkMailPermit?.reload();
                    loading.onChange(false);
                },
            })],
            ({loading, editPermitModal}) => next({
                header: {
                    title: "Manage Bulk Mail Permit",
                    action: {
                        label: bulkMailPermit?.value?.idPrimary || loading.value || bulkMailPermit?.loading ? "Edit" : "Add",
                        xdQaName: "Manage Bulk Mail Permit",
                        onClick: () => editPermitModal.show({
                            data: [
                                {"label": <div>Line 1 <span style={{color: "red"}}>*</span></div>, "value": bulkMailPermit?.value?.line1 ?? "", "mapping": "line1", "maxLength": "40"},
                                {"label": <div>Line 2 <span style={{color: "red"}}>*</span></div>, "value": bulkMailPermit?.value?.line2 ?? "", "mapping": "line2", "maxLength": "40"},
                                {"label": <div>Line 3 <span style={{color: "red"}}>*</span></div>, "value": bulkMailPermit?.value?.line3 ?? "", "mapping": "line3", "maxLength": "40"},
                                {"label": "Line 4", "value": bulkMailPermit?.value?.line4 ?? "", "mapping": "line4", "maxLength": "40"},
                                {"label": "Line 5", "value": bulkMailPermit?.value?.line5 ?? "", "mapping": "line5", "maxLength": "40"},
                            ],
                        }),
                    },
                },
                content: (
                    <div className="bulk-mail-permit-1o7">
                        {loading.value || bulkMailPermit?.loading ? (
                            <div className="loading">
                                <i className="fa fa-spinner fa-pulse"/>
                            </div>
                        ) : (
                            !bulkMailPermit?.value?.idPrimary ? (
                                NoPermit()
                            ) : (
                                <div className="permit">
                                    {Object.keys(bulkMailPermit?.value).filter(v => v && v.includes("line")).map((line) => (
                                        <span key={line} className="value" xd-qa-type="label" xd-qa-name={line}>
                                            {bulkMailPermit?.value[line]}
                                        </span>
                                    ))}
                                </div>
                            )
                        )}
                    </div>
                ),
            }),
        ),
    })
);

export const bulkMailPermitIsAllowed = (userLevel, company, session) => {    
    if(COMPANY_LEVEL === userLevel) {
        return (BULK_MAIL_PERMIT_PROHIBITED != company?.prefs?.allowBulkMailPermit)
    }

    const allowed = session?.data?.branch?.allowBulkMailPermit
    if(BULK_MAIL_PERMIT_USE_COMPANY == allowed) {
        return (BULK_MAIL_PERMIT_PROHIBITED != company?.prefs?.allowBulkMailPermit)
    }
    
    return !!allowed
}